/**
 * AgentBar
 *
 * An ambient horizontal strip that surfaces contextually relevant agents
 * for the current page. Replaces the pattern of throwing disruptive
 * alert banners every time an agent could run.
 *
 * Design intent:
 *   - Always present when there are agents, never demanding attention
 *   - Agents are represented by their custom glyphs (not generic icons)
 *   - The pill shape and circular glyphs make agents visually distinct
 *     from any other control in the interface
 *   - Clicking opens a modal that explains what the agent can do and
 *     offers to run it — no cold-decision forcing
 *
 * Usage:
 *   <AgentBar agents={[atcDescriptor, twinsDescriptor]} />
 */

import { useState } from 'react';
import { AgentGlyph } from './AgentGlyph';
import { AgentModal } from './AgentModal';
import type { AgentDescriptor, AgentColorScheme } from './types';

// ── Pill ──────────────────────────────────────────────────────────────────────

const PILL_HOVER: Record<AgentColorScheme, string> = {
  amber:   'hover:border-[var(--mustard)]   hover:bg-[var(--mustard-dim)]   hover:text-[var(--mustard)]',
  indigo:  'hover:border-[var(--slate)]  hover:bg-[var(--slate-dim)]  hover:text-[var(--text-ter)]',
  emerald: 'hover:border-[var(--green)] hover:bg-[var(--green-dim)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]',
  rose:    'hover:border-[var(--coral)]    hover:bg-[var(--coral-dim)]    hover:text-[var(--coral)]',
};

const RUNNING_PILL: Record<AgentColorScheme, string> = {
  amber:   'border-[var(--mustard)]   bg-[var(--mustard-dim)]   text-[var(--mustard)]',
  indigo:  'border-[var(--slate)]  bg-[var(--slate-dim)]  text-[var(--text-sec)]',
  emerald: 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  rose:    'border-[var(--coral)]    bg-[var(--coral-dim)]    text-[var(--coral)]',
};

interface AgentPillProps {
  agent: AgentDescriptor;
  onClick: () => void;
}

function AgentPill({ agent, onClick }: AgentPillProps) {
  const hover    = PILL_HOVER[agent.colorScheme]   ?? PILL_HOVER.amber;
  const running  = RUNNING_PILL[agent.colorScheme] ?? RUNNING_PILL.amber;

  const pillClass = agent.isRunning
    ? `${running} cursor-default`
    : agent.disabled
      ? 'bg-[var(--bg-raised)] text-[var(--text-ter)] border-[var(--rule)] cursor-not-allowed opacity-70'
      : `bg-[var(--bg-raised)] text-[var(--text-sec)] ${hover} cursor-pointer`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={Boolean(agent.disabled || agent.isRunning)}
      className={`
        group flex items-center gap-2 px-2.5 py-1.5
        rounded-full border border-[var(--rule)]
        text-xs font-medium
        shadow-sm hover:shadow
        transition-all duration-150
        select-none
        ${pillClass}
      `}
      title={agent.disabled ? agent.disabledReason : agent.tagline}
    >
      <AgentGlyph
        type={agent.glyphType}
        colorScheme={agent.colorScheme}
        size={22}
        pulse={!agent.isRunning && !agent.disabled}
      />
      <span>{agent.name}</span>
      {agent.isRunning && (
        <span className="ml-0.5 flex h-1.5 w-1.5 rounded-full bg-current opacity-75 animate-pulse" />
      )}
    </button>
  );
}

// ── AgentBar ──────────────────────────────────────────────────────────────────

export interface AgentBarProps {
  agents: AgentDescriptor[];
  /**
   * Visual treatment of the container.
   *   - 'strip'  (default): full-bleed strip with a bottom border — used inside
   *     ItemViewShell, directly under the page header.
   *   - 'inline': a self-contained rounded card that sits in a normal padded
   *     page body (e.g. between a PageHeader and the page content).
   */
  variant?: 'strip' | 'inline';
  /** Extra className on the outer container */
  className?: string;
}

/** Chrome on the outer element — full-bleed for the strip, a card for inline. */
const CONTAINER_VARIANT: Record<NonNullable<AgentBarProps['variant']>, string> = {
  strip:  'border-b border-[var(--rule)] bg-[var(--surface)]/60 flex-shrink-0',
  inline: 'px-4 py-2  border border-[var(--rule)] bg-[var(--surface)]/60',
};

/**
 * Padding on the row of pills. The strip sits between ItemViewShell's header
 * and its main content, both of which centre their contents in a max-w-5xl
 * column with px-6 gutters — the row matches that column so the AGENTS label
 * lines up with the title above it and the tab strip below it, while the
 * border and tint stay full-bleed.
 */
const CONTENT_VARIANT: Record<NonNullable<AgentBarProps['variant']>, string> = {
  strip:  'max-w-5xl mx-auto px-6 py-2',
  inline: '',
};

export function AgentBar({ agents, variant = 'strip', className = '' }: AgentBarProps) {
  const [activeAgent, setActiveAgent] = useState<AgentDescriptor | null>(null);

  if (agents.length === 0) return null;

  return (
    <>
      <div className={`${CONTAINER_VARIANT[variant]} ${className}`}>
        <div className={`flex items-center gap-2 flex-wrap ${CONTENT_VARIANT[variant]}`}>
          <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest mr-0.5 select-none">
            Agents
          </span>
          {agents.map((agent) => (
            <AgentPill
              key={agent.id}
              agent={agent}
              onClick={() => agent.customOnClick ? agent.customOnClick() : setActiveAgent(agent)}
            />
          ))}
        </div>
      </div>

      {activeAgent && (
        <AgentModal
          agent={activeAgent}
          onClose={() => setActiveAgent(null)}
        />
      )}
    </>
  );
}
